import { brusselsDateOf } from "@bivak/domain";
import { useQuery } from "@tanstack/react-query";
import { BeheerMaand } from "../../components/beheer/BeheerMaand.js";
import { nl } from "../../copy/nl.js";
import { ApiError, fetchAdminBlokkades, fetchAdminBookings } from "../../lib/api.js";
import { bezettingPerDag, kalenderMaanden } from "../../lib/beheerKalender.js";

const LEGENDE = [
  { key: "option", swatch: "border-[#c98f1d] bg-[#e3a92c]" },
  { key: "confirmed", swatch: "border-dennen-diep bg-dennen" },
  { key: "stayed", swatch: "border-[#48708d] bg-[#5b87a5]" },
  { key: "settled", swatch: "border-[#8f8d7c] bg-[#a3a18e]" },
  { key: "blokkade", swatch: "hatch border-[#b3a887]" },
] as const;

/** The admin calendar: WHO holds each day — the public one never says. */
export function BeheerKalenderPage() {
  const bookings = useQuery({
    queryKey: ["admin-bookings"],
    queryFn: fetchAdminBookings,
    retry: (count, err) => (err instanceof ApiError && err.status < 500 ? false : count < 1),
  });
  const blokkades = useQuery({
    queryKey: ["admin-blokkades"],
    queryFn: fetchAdminBlokkades,
    retry: (count, err) => (err instanceof ApiError && err.status < 500 ? false : count < 1),
  });

  if (bookings.isPending || blokkades.isPending) {
    return <p className="py-16 text-center text-mist">{nl.algemeen.laden}</p>;
  }
  if (bookings.isError || blokkades.isError || !bookings.data || !blokkades.data) {
    return <p className="py-16 text-center text-mist">{nl.algemeen.fout}</p>;
  }

  const dagen = bezettingPerDag(bookings.data.bookings, blokkades.data.blokkades);
  const todayIso = brusselsDateOf(new Date()).toString();
  const maanden = kalenderMaanden(dagen, todayIso);

  // Today first: the window opens up to three months in the past for
  // settlement work, but on a phone that was two full month cards of scroll
  // before "now" (user feedback, 2026-08-21). The past keeps its access,
  // folded shut below the future.
  const huidigeMaand = todayIso.slice(0, 7);
  const actueel = maanden.filter((maand) => maand.toString().slice(0, 7) >= huidigeMaand);
  const verleden = maanden.filter((maand) => maand.toString().slice(0, 7) < huidigeMaand);

  const maandGrid = (lijst: typeof maanden) => (
    <div className="grid grid-cols-1 gap-x-6 gap-y-7 sm:grid-cols-2 xl:grid-cols-3">
      {lijst.map((maand) => (
        <BeheerMaand key={maand.toString()} month={maand} dagen={dagen} todayIso={todayIso} />
      ))}
    </div>
  );

  return (
    <div>
      <h1 className="mb-1 font-bold font-display text-2xl tracking-tight">{nl.beheer.kalender}</h1>
      <p className="mb-4 text-[13px] text-mist">{nl.beheer.kalenderScherm.uitleg}</p>
      <div className="mb-6 flex flex-wrap gap-x-4 gap-y-1.5">
        {LEGENDE.map((item) => (
          <span key={item.key} className="flex items-center gap-1.5 text-[12px]">
            <i className={`block h-3.5 w-3.5 rounded border ${item.swatch}`} />
            {nl.beheer.kalenderScherm.legende[item.key]}
          </span>
        ))}
      </div>
      {maandGrid(actueel)}
      {verleden.length > 0 && (
        <details className="mt-7">
          <summary className="cursor-pointer font-mono font-semibold text-[11px] text-rood uppercase tracking-widest">
            {nl.beheer.kalenderScherm.eerdereMaanden(verleden.length)}
          </summary>
          <div className="mt-4">{maandGrid(verleden)}</div>
        </details>
      )}
    </div>
  );
}
